/**
 * IndividualIntake.jsx
 * ---------------------------------------------------------------------------
 * Section 3. The demand-side intake form. Posts to /api/case-intake and
 * displays the structured result (tags, signal score, classification, queue,
 * generated summary) on success.
 */

import React, { useState } from "react";
import Section from "../ui/Section.jsx";
import { H2, Lede } from "../ui/Heading.jsx";
import Button from "../ui/Button.jsx";
import Field from "../ui/Field.jsx";
import Meta from "../ui/Meta.jsx";
import { api } from "../../lib/apiClient.js";
import { tokens } from "../../lib/tokens.js";

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  state: "",
  caseType: "",
  description: "",
  incidentDate: "",
  damages: "",
  documentation: "",
  priorAttorney: "",
};

const CASE_TYPES = [
  { value: "Personal Injury", label: "Personal Injury" },
  { value: "Employment", label: "Employment" },
  { value: "Contract Dispute", label: "Contract Dispute" },
  { value: "Family", label: "Family" },
  { value: "Consumer", label: "Consumer / Class" },
  { value: "Other", label: "Other" },
];

const DOC_OPTIONS = [
  { value: "Yes, available", label: "Yes, available" },
  { value: "Partial", label: "Partial" },
  { value: "None", label: "None" },
];

const ATTORNEY_OPTIONS = [
  { value: "No", label: "No" },
  { value: "Yes, previously", label: "Yes, previously" },
];

export default function IndividualIntake() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await api.submitCase(form);
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error || "Submission failed.");
      return;
    }
    setResult(res.record);
  };

  const reset = () => {
    setResult(null);
    setForm(EMPTY_FORM);
  };

  return (
    <Section id="individuals" label="II · Individuals">
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1.1fr",
          gap: 96,
          alignItems: "start",
        }}
      >
        {/* Copy column */}
        <div style={{ position: "sticky", top: 120 }}>
          <H2>Submit your case for structured review.</H2>
          <Lede>
            We organize your information so attorneys can review it efficiently
            and respond with clarity.
          </Lede>

          <div
            style={{
              marginTop: 64,
              borderLeft: `1px solid ${tokens.divider}`,
              paddingLeft: 24,
            }}
          >
            {[
              "Your submission is organized into a standardized case profile.",
              "Qualified firms in your jurisdiction are notified.",
              "You retain full control of your matter and your choice of counsel.",
            ].map((t, i) => (
              <div key={i} style={{ display: "flex", gap: 16, marginBottom: 20 }}>
                <div
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: 18,
                    color: tokens.accent,
                    fontStyle: "italic",
                    minWidth: 24,
                  }}
                >
                  {i + 1}.
                </div>
                <div
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 14,
                    color: tokens.inkSoft,
                    lineHeight: 1.6,
                  }}
                >
                  {t}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Form card */}
        <div
          style={{
            background: tokens.bg,
            border: `1px solid ${tokens.divider}`,
            padding: "48px 52px",
          }}
        >
          {!result ? (
            <IntakeForm
              form={form}
              update={update}
              submit={submit}
              error={error}
              submitting={submitting}
            />
          ) : (
            <IntakeResult record={result} onReset={reset} />
          )}
        </div>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
function IntakeForm({ form, update, submit, error, submitting }) {
  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          paddingBottom: 24,
          borderBottom: `1px solid ${tokens.divider}`,
          marginBottom: 36,
        }}
      >
        <div
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 22,
            color: tokens.ink,
          }}
        >
          Case Intake Form
        </div>
        <div
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: 11,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: tokens.inkSoft,
          }}
        >
          Confidential
        </div>
      </div>

      <form onSubmit={submit}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 28 }}>
          <Field label="Full Name" name="name" value={form.name} onChange={update} />
          <Field label="Email" name="email" type="email" value={form.email} onChange={update} />
          <Field label="Phone" name="phone" value={form.phone} onChange={update} />
          <Field
            label="State"
            name="state"
            value={form.state}
            onChange={update}
            placeholder="e.g., California"
          />
        </div>

        <Field
          label="Case Type"
          name="caseType"
          value={form.caseType}
          onChange={update}
          options={CASE_TYPES}
        />

        <Field
          label="Description"
          name="description"
          value={form.description}
          onChange={update}
          textarea
          placeholder="Describe the facts of your matter."
        />

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 28 }}>
          <Field
            label="Incident Date"
            name="incidentDate"
            type="date"
            value={form.incidentDate}
            onChange={update}
          />
          <Field
            label="Damages"
            name="damages"
            value={form.damages}
            onChange={update}
            placeholder="Scope and nature"
          />
          <Field
            label="Documentation"
            name="documentation"
            value={form.documentation}
            onChange={update}
            options={DOC_OPTIONS}
          />
          <Field
            label="Prior Attorney"
            name="priorAttorney"
            value={form.priorAttorney}
            onChange={update}
            options={ATTORNEY_OPTIONS}
          />
        </div>

        {error && (
          <div
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 13,
              color: "#9B2C2C",
              marginBottom: 16,
              paddingLeft: 12,
              borderLeft: "2px solid #9B2C2C",
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            marginTop: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 11,
              color: tokens.inkSoft,
              maxWidth: 320,
              lineHeight: 1.5,
            }}
          >
            Submission is organized for attorney review. This is not legal
            advice.
          </div>
          <Button type="submit" disabled={submitting}>
            {submitting ? "Submitting…" : "Submit Case"}
          </Button>
        </div>
      </form>
    </>
  );
}

// ---------------------------------------------------------------------------
function IntakeResult({ record, onReset }) {
  const colorMap = {
    HOT: "#9B2C2C",
    WARM: "#B7791F",
    COLD: tokens.inkSoft,
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          paddingBottom: 24,
          borderBottom: `1px solid ${tokens.divider}`,
          marginBottom: 36,
        }}
      >
        <div
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 22,
            color: tokens.ink,
          }}
        >
          Case Received
        </div>
        <div
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: 11,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: colorMap[record.classification],
          }}
        >
          {record.classification} · {record.queue}
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 32,
          marginBottom: 32,
        }}
      >
        <Meta label="Case Type" value={record.summary.caseType} />
        <Meta label="Signal Score" value={`${record.score} / 100`} />
      </div>

      <div style={{ marginBottom: 32 }}>
        <Meta label="Summary" value={record.summary.summary || "—"} />
      </div>

      <div style={{ marginBottom: 36 }}>
        <div
          style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: 11,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: tokens.inkSoft,
            marginBottom: 12,
          }}
        >
          Key Facts
        </div>
        {record.summary.keyFacts.length === 0 ? (
          <div
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 14,
              color: tokens.inkSoft,
            }}
          >
            —
          </div>
        ) : (
          <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
            {record.summary.keyFacts.map((f, i) => (
              <li
                key={i}
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 14,
                  color: tokens.ink,
                  padding: "10px 0",
                  borderTop:
                    i === 0 ? "none" : `1px solid ${tokens.divider}`,
                }}
              >
                {f}
              </li>
            ))}
          </ul>
        )}
      </div>

      {record.tags.length > 0 && (
        <div style={{ marginBottom: 36 }}>
          <div
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 11,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: tokens.inkSoft,
              marginBottom: 12,
            }}
          >
            Tags
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {record.tags.map((t) => (
              <span
                key={t}
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 11,
                  letterSpacing: "0.04em",
                  color: tokens.ink,
                  border: `1px solid ${tokens.hairline}`,
                  padding: "4px 10px",
                }}
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      )}

      <Button variant="ghost" onClick={onReset}>
        Submit Another Case
      </Button>
    </div>
  );
}
